import { useState } from 'react';

import { MediaFrame, PointerParallax, ScrollReveal, SplitReveal } from '@/components/motion';
import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { cn } from '@ibas/ui';

const FEATURE_VIDEO = '/media/videos/onsite-experience-2.mp4';
const FEATURE_POSTER = '/media/images/campus/classroom-group-presentation-laptops.webp';

interface Step {
  title: string;
  description: string;
}

/**
 * The three benefits. Copy is drawn strictly from the IBAS content bank — the
 * dual-award angle line and the "why study" quote are used verbatim.
 */
const STEPS: readonly Step[] = [
  {
    title: 'Dual awards',
    description:
      'One programme. Two qualifications. Broader leverage from a single course of study — recognition that works harder for your career.',
  },
  {
    title: 'Study your way',
    description:
      'Self-study, live online, blended or on-campus — build the programme around the rhythm of your life, not the other way round.',
  },
  {
    title: 'Progress globally',
    description:
      'Advancing in today’s business environment requires more than a qualification. It requires recognition, credibility, and the ability to operate across borders.',
  },
];

export interface FeatureStickyProps {
  className?: string;
  /** Optional id for in-page anchoring. */
  id?: string;
}

/**
 * FeatureSticky — "One programme. Two qualifications." A single-screen
 * two-column feature: a height-capped media plate on the left, the header plus
 * three benefit steps on the right, all visible within one viewport.
 *
 * Interactivity (both tied to meaning, no scroll distance required):
 *  - Pointer hover: hovering/focusing a step sets it "active" (default 0, so one
 *    is always active) — the active step gets a gold accent, and a caption
 *    overlaid on the media crossfades to name that same step, keeping visual and
 *    copy in lockstep as the user moves through the list.
 *  - Pointer parallax: the media sits inside PointerParallax for a subtle
 *    cursor-driven drift, giving the plate real spatial depth. Disabled on
 *    touch `(hover: none)`.
 *
 * Reduced motion: no active accent or caption crossfade — every step + the
 * media caption ("One programme. Two qualifications.") render fully visible and
 * static. The ScrollReveal entrance still resolves to its visible end-state.
 */
export function FeatureSticky({ className, id }: FeatureStickyProps) {
  const [activeStep, setActiveStep] = useState(0);
  const reduced = useReducedMotion();

  return (
    <section
      id={id}
      className={cn('flex min-h-[100svh] items-center bg-background py-12 md:py-16', className)}
    >
      <div className="mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-10 px-6 lg:grid-cols-2 lg:gap-12">
        {/* Media plate — height-capped so the 4:5 frame never exceeds ~68vh
            (max-width derived from viewport height keeps the aspect intact). */}
        <div className="mx-auto w-full max-w-[54vh]">
          <PointerParallax strength={16}>
            <div data-parallax data-depth="0.6">
              <MediaFrame
                image={FEATURE_POSTER}
                video={FEATURE_VIDEO}
                alt="Students with laptops watching a presentation in an IBAS classroom, representing the IBAS Flexi-Learn and on-campus experience"
                aspect="aspect-[4/5]"
                position="50% 40%"
                overlay={55}
                rounded="rounded-2xl"
              >
                {!reduced && (
                  <div className="relative min-h-[4.75rem] px-6 pb-6 md:px-8 md:pb-8">
                    {STEPS.map((step, index) => (
                      <div
                        key={step.title}
                        aria-hidden={index !== activeStep}
                        className={cn(
                          'absolute inset-x-6 bottom-6 flex flex-col gap-1 transition-opacity duration-700 ease-out md:inset-x-8 md:bottom-8',
                          index === activeStep ? 'opacity-100' : 'opacity-0',
                        )}
                      >
                        <span className="font-label text-xs tracking-[0.2em] text-brand-gold-light uppercase">
                          {`0${index + 1} / 0${STEPS.length}`}
                        </span>
                        <p className="font-display text-2xl leading-tight text-white md:text-3xl">
                          {step.title}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
                {reduced && (
                  <div className="absolute inset-x-6 bottom-6 md:inset-x-8 md:bottom-8">
                    <p className="font-display text-2xl leading-tight text-white md:text-3xl">
                      One programme. Two qualifications.
                    </p>
                  </div>
                )}
              </MediaFrame>
            </div>
          </PointerParallax>
        </div>

        {/* Header + steps column. */}
        <div>
          <header className="mb-8 max-w-2xl md:mb-10">
            <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">Why IBAS</p>
            <SplitReveal
              as="h2"
              by="lines"
              className="mt-3 font-display text-4xl leading-tight text-foreground md:text-5xl"
            >
              Built for career mobility.
            </SplitReveal>
          </header>

          <div className="flex flex-col gap-6 md:gap-8">
            {STEPS.map((step, index) => {
              const isActive = !reduced && index === activeStep;
              return (
                <ScrollReveal key={step.title} from="up">
                  <div
                    className="flex gap-5"
                    onMouseEnter={() => setActiveStep(index)}
                    onFocus={() => setActiveStep(index)}
                    tabIndex={reduced ? undefined : 0}
                  >
                    <span
                      className={cn(
                        'flex size-11 shrink-0 items-center justify-center rounded-full border font-display text-lg transition-colors duration-500 motion-reduce:transition-none',
                        isActive
                          ? 'border-brand-gold bg-brand-gold/10 text-brand-gold'
                          : 'border-border text-muted-foreground',
                      )}
                    >
                      {`0${index + 1}`}
                    </span>
                    <div className="pt-1">
                      <h3
                        className={cn(
                          'font-display text-2xl leading-tight font-semibold transition-colors duration-500 motion-reduce:transition-none md:text-3xl',
                          isActive ? 'text-brand-gold' : 'text-foreground',
                        )}
                      >
                        {step.title}
                      </h3>
                      <p className="mt-2 max-w-md text-base leading-relaxed text-muted-foreground">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
